import React, { useState, useEffect, useMemo } from 'react';
import { Box, Stack, Typography, Select, Grid, Button, MenuItem, IconButton } from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import UploadModal from '../../components/Modals/Upload';
import RecentlyUploadedTable from './RecentlyUploadTable';
import { downloadTemplate, fetchFileUploadList, uploadFile } from './services';
import PageHeader from '../../components/PageHeader/PageHeader';
import { useSelector } from 'react-redux';

// Reusable Components
const CardItem = ({ card, index, handleDownloadTemplate, files }) => (
  <Grid item xs={12} sm={6} md={4} key={card.id}>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ border: '1px solid #D7E5F8', borderRadius: '8px', padding: '16px', height: '5rem' }}>
      <Stack direction="row" gap="8px">
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40px' }}>
          <img src={card.cardImage} alt={card.cardHeader} width="40px" height="40px" />
        </Box>
        <Stack>
          <Typography fontWeight={700} sx={{ display: 'flex', alignItems: 'center' }}>{index}. {card.cardHeader} { files.includes(card.fileName) && <CheckCircleIcon sx={{ color: '#008553', fontSize: '20px', marginLeft: '5px' }} />}</Typography>
          <Typography sx={{ fontSize: 12 }}>{card.cardContent}</Typography>
        </Stack>
      </Stack>

      <IconButton
        aria-label={`Download ${card.cardHeader} Template`}
        data-testid={`download-template-${card.id}`} // Add a unique test ID
        size="medium"
        sx={{
          borderRadius: '4px',
          backgroundColor: '#EFF5FC',
          border: '1px solid #D7E5F8',
          padding: '4px',
          color: '#005CBC',
          width: '40px',
          height: '40px'
        }}
        onClick={() => handleDownloadTemplate(card.fileName)}>
        <DownloadOutlinedIcon sx={{ color: '#005CBC', fontSize: '20px' }} />
      </IconButton>
    </Stack>
  </Grid>
);

const SelectInput = ({ label, value, options, onChange, disabled }) => (
  <Stack spacing={1} direction="column">
    <Typography variant="extraSmall" sx={{ color: '#616161' }}>
      {label}
    </Typography>
    <Select
      value={value}
      onChange={onChange}
      sx={{ width: '100%', height: '32px', margin: 0 }}
      disabled={disabled}>
      {options.map((option, i) => (
        <MenuItem disabled={option.disabled} key={option.value || option} value={option.value || option}>
          {i+1}. {option?.label || option}
        </MenuItem>
      ))}
    </Select>
  </Stack>
);

const SelectFilterComponent = ({ handleOpen, getOptions, recentUploadedFiles }) => {
  const selectedBu = useSelector((state) => state?.cycle?.selectedBu);
  const market = selectedBu;
  const [dataType, setDataType] = useState('');
  const [cardLists, setCardLists] = useState({ manual: [], automated: [] }); // Manage state for CardLists
  const getAvatar = (imageName) => {
    try {
      return require(`../../assests/images/${imageName}.svg`);
    } catch (error) {
      console.error(`Image not found for: ${imageName}`);
      return null;
    }
  };

  const fileUploadSequence = ["Region", "Channel", "Material", "Sales", "Profit", "Waste", "VGP"];

  useEffect(() => {
    const fetchCardLists = async () => {
      try {
        const response = await fetchFileUploadList(market || selectedBu);       
        if (response) {
          const sortedResponseManual = [...response.manual].sort((firstValue, secondValue) => {
            const indexA = fileUploadSequence.indexOf(firstValue.cardHeader);
            const indexB = fileUploadSequence.indexOf(secondValue.cardHeader);
            return indexA - indexB;
          });
          
          const sortedResponseAutomated = [...response.automated].sort((firstValue, secondValue) => {
            const indexA = fileUploadSequence.indexOf(firstValue.cardHeader);
            const indexB = fileUploadSequence.indexOf(secondValue.cardHeader);
            return indexA - indexB;
          });
        
          const updatedCardLists = {
            manual: sortedResponseManual.map((card) => ({
              ...card,
              cardImage: getAvatar(card.cardImage)
            })),
            automated: sortedResponseAutomated.map((card) => ({
              ...card,
              cardImage: getAvatar(card.cardImage)
            }))
          };
          setCardLists(updatedCardLists);
        } else {
          setCardLists({ manual: [], automated: [] });
        }
      } catch (error) {
        console.error('Error fetching card lists:', error);
      }
    };

    if (market || selectedBu) {
      fetchCardLists();
    }
  }, [market, selectedBu]);

  const cardList = useMemo(
    () => (cardLists.automated.length > 0 ? cardLists.automated : cardLists.manual),
    [cardLists]
  );

  const options = useMemo(
    () =>
      cardList.map(({ id, fileName }) => ({
          value: fileName,
        label: id
      })),
    [cardList]
  );
  getOptions(options);

  const uploadOptions = useMemo(() => {
    const uplOp = [];
    if(options && options.length > 0){
      let isLastEnabled = true;
      for(let option of options){
        let isEnabled = true;
        if(recentUploadedFiles) {
          isEnabled =
          recentUploadedFiles.includes(option.value) ||
          (
            option.value === "minp_tpo_mstr_mtrl"
              ? recentUploadedFiles.includes("minp_tpo_map_rgn") || recentUploadedFiles.includes("minp_tpo_map_chnl")
              : option.value === "minp_tpo_map_rgn" || option.value === "minp_tpo_map_chnl" 
                || option.value === "minp_tpo_upc_vgppercent" || isLastEnabled
          );
          isLastEnabled = recentUploadedFiles.includes(option.value);
        }
        uplOp.push({
          ...option,
          disabled: !isEnabled
        });
      }
    }
    return uplOp;
  }, [options, recentUploadedFiles]);

 
  // Auto select first option by default
  // useEffect(() => {
  //   if (options.length > 0 && options[0]) {
  //     setDataType(options[0].value);
  //   }
  // }, [options, market]);

  useEffect(() => {
  if (!dataType && uploadOptions.length > 0) {
    const firstEnabled = uploadOptions.find((opt) => !opt.disabled);
    if (firstEnabled) {
      setDataType(firstEnabled.value);
    }
  }
}, [uploadOptions, market]);

  const handleDownloadTemplate = (id) => downloadTemplate(market, id);

  return (
    <Grid container spacing={2} sx={{ height: '272px' }}>
      <Grid item xs sx={{ height: '100%' }}>
        <Stack
          gap={2}
          sx={{
            p: 2,
            borderRadius: '8px',
            backgroundColor: '#FBFCFE',
            boxShadow: '4px 0px 24px -2px #005CBC14',
            height: '100%'
          }}>
          <Typography variant="h6" fontWeight={700}>
            Download Template
          </Typography>
          <Grid container spacing={2} sx={{ height: '100%', alignItems: 'stretch' }}>
            {cardList.map((card, index) => (
              <CardItem key={card.id} files={recentUploadedFiles} index={index + 1} card={card} handleDownloadTemplate={handleDownloadTemplate} />
            ))}
          </Grid>
        </Stack>
      </Grid>

      <Grid item sx={{ height: '100%' }}>
        <Stack
          gap={2}
          sx={{
            p: '24px',
            borderRadius: '8px',
            backgroundColor: '#FBFCFE',
            boxShadow: '4px 0px 24px -2px #005CBC14',
            width: '230px',
            height: '100%'
          }}>
          <Typography variant="h6" sx={{ color: '#3A3A3A' }}>
            Upload File
          </Typography>
          <Typography sx={{fontSize: "13px" }}>{selectedBu}</Typography>
          {uploadOptions && uploadOptions.length > 0 && <SelectInput
              label="Type of data"
              // value={uploadOptions.findLast(option => !option.disabled).value}
              value={dataType}
              options={uploadOptions}
              onChange={(e) => setDataType(e.target.value)}
              disabled={!selectedBu}
            />
          }
          <Button
            data-testid="upload-file-button"
            variant="contained"
            color="primary"
            size="small"
            endIcon={<FileUploadIcon />}
            onClick={() => handleOpen({ selectedBu, dataType })}
            disabled={!(selectedBu && dataType)}
            fullWidth>
            Upload File
          </Button>
        </Stack>
      </Grid>
    </Grid>
  );
};

// Main component
const ManualUpload = () => {
  const [open, setOpen] = useState(false);
  const [filterObj, setFilterObj] = useState({});
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [timeoutNo, setTimeoutNo] = useState(10);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const [optionByBU, setOptionByBU] = useState([]);
  const handleOpen = (obj) => {
    setOpen(true);
    setFilterObj(obj);
  };
  const handleClose = () => {
    setOpen(false);
    setTimeoutNo(10);
  };

  const handleUpload = async (formData, onUploadProgress) => {
    try {
      if (formData) {
        return await uploadFile(formData, onUploadProgress);
      }
    } finally {
      setRefreshTrigger((prev) => !prev);
    }
  };

  const successFullyUploadedFiles = (dataFromChild) => {
    const fileNames = dataFromChild
      .filter(file => file.uploadStatus === 'Success')
      .map(file => file.fileName);
    setUploadedFiles(fileNames);
  }

  return (
    <Stack gap="24px" sx={{ padding: '24px', width: '100%' }}>
      <Box>
        <PageHeader
          pageHeader="PO Manual Data Upload"
          showCycle={false}
          showTimeFrame={false}
          displayInfo
          infoMsg={'Please upload the file in the sequence listed. Upload can be configured for either region or channel'}
        />

        <Typography variant="body1" sx={{ color: '#616161' }}>
          Here you can download a template to populate with your local product portfolio data, and
          upload it to initiate PO cycle analysis.
        </Typography>
      </Box>
      <SelectFilterComponent handleOpen={handleOpen} recentUploadedFiles={uploadedFiles} getOptions={(val) => setOptionByBU(val)} />
      <RecentlyUploadedTable refreshTrigger={refreshTrigger} optionByBU={optionByBU} uploadedData={successFullyUploadedFiles} />
      <UploadModal
        open={open}
        handleClose={handleClose}
        onUploadFile={handleUpload}
        filterObj={filterObj}
        mode="single"
        pageId="manualUpload"
        timeoutNo={timeoutNo}
        setTimeoutNo={(timeoutNo) => setTimeoutNo(timeoutNo)}
      />
    </Stack>
  );
};

export default ManualUpload;
